import React, { useState, useContext, useEffect } from "react";
import { Layout, PageActions } from "@shopify/polaris";
import { useMutation, useQuery } from "react-apollo";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";
import constant from "lib/constant/constant";
import { Banner, SkeletonAnnotated } from "lib/components";
import { baseHelper, errorHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import { NotFound } from "layout/private/components";

import { GET_VENDOR_VARIANT } from "app/productOld/apollo/queries";
import { UPDATE_VARIANT, UPLOAD_IMAGE } from "app/productOld/apollo/mutations";

import { TitleEdit, Image, Price, Inventory, Shipping } from "./subFeatureItems";

import validate from "../yup";

const {
  value: { MAX_MEASUREMENT = 999 },
  SKU,
} = constant;

const ProviderVariantEdit = () => {
  const { match, history, cms } = useContext(PrivateContext);
  const { params } = match;
  const { productId, id: variantId } = params;
  const { data: variantData, loading, error } = useQuery(GET_VENDOR_VARIANT, {
    variables: { input: { id: productId } },
  });

  const [updateVariant, { loading: updateLoading, error: updateVariantError }] = useMutation(UPDATE_VARIANT, {
    refetchQueries: [
      {
        query: GET_VENDOR_VARIANT,
        variables: { input: { id: productId } },
      },
    ],
  });
  const [uploadImage, { loading: imageLoading, error: uploadImageError }] = useMutation(UPLOAD_IMAGE);
  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
  });
  const [variantValue, setVariantValue] = useState({});
  const [image, setImage] = useState();
  const [options, setOptions] = useState({
    option1Val: null,
    option2Val: null,
    option3Val: null,
  });

  const [productImages, setProductImages] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [submitButton, setSubmitState] = useState({
    isReadyToSubmit: false,
  });

  const { gql } = constant;

  const handleValidation = async (field, value) => {
    const validationError = await validate(field, value);
    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
  };

  useEffect(() => {
    if (error || uploadImageError || updateVariantError) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(error || uploadImageError || updateVariantError),
      });
    }
  }, [error, uploadImageError, updateVariantError]);

  useEffect(() => {
    const { option1Val, option2Val, option3Val } = options;
    let isAllValuesFilled = false;
    if (option1Val) {
      isAllValuesFilled = option1Val && variantValue.option1Val;
    }
    if (option2Val) {
      isAllValuesFilled = option2Val && variantValue.option2Val;
    }
    if (option3Val) {
      isAllValuesFilled = option3Val && variantValue.option3Val;
    }

    const isAnyValidationError =
      errorMessage &&
      !!(
        (errorMessage.size && option1Val) ||
        (errorMessage.color && option2Val) ||
        (errorMessage.material && option3Val)
      );
    setSubmitState((prevState) => ({
      ...prevState,
      isReadyToSubmit: isAllValuesFilled && !isAnyValidationError,
    }));
  }, [variantValue, errorMessage, options]);

  useEffect(() => {
    const responseData = baseHelper.getResponseData(variantData, gql.GET_PRODUCT);
    if (responseData) {
      const { variants = [], images = [] } = responseData || {};
      if (variants.length < variantId || variantId <= 0) {
        return <NotFound />;
      }
      const variantOption = variants[variantId - 1];
      setOptions({
        option1: variantOption.option1,
        option2: variantOption.option2,
        option3: variantOption.option3,
        option1Val: variantOption.option1Val,
        option2Val: variantOption.option2Val,
        option3Val: variantOption.option3Val,
      });
      setProductImages(images || []);
      setImage((variantOption.image && [{ url: variantOption.image }]) || []);
      setVariantValue(variants[variantId - 1]);
    }
  }, [variantData, gql, variantId]);

  if (loading) {
    return <SkeletonAnnotated />;
  }

  const variantUpdate = (updateValues) => {
    updateVariant({ variables: { input: updateValues } })
      .then((res) => {
        const resError = baseHelper.getResponseError(res.data, gql.EDIT_VARIANT);
        const bannerData = {};
        bannerData.url = true;
        bannerData.status = constant.SUCCESS;
        bannerData.title = cms("message.success.update");
        bannerData.action = {
          content: cms("message.success.reviewProduct"),
          onAction: () => history.push(`/products/edit/${productId}`),
        };
        if (resError) {
          bannerData.url = false;
          bannerData.title = resError;
          bannerData.status = constant.CRITICAL;
          banner.action = null;
        }
        setBanner({ isOpen: true, status: bannerData.status, title: bannerData.title, action: bannerData.action });
      })
      .catch((exception) => {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: errorHelper.parse(exception),
        });
      });
  };

  const onSubmit = async () => {
    const result = variantValue;
    await handleValidation("option1", result.option1Val);
    await handleValidation("option2", result.option2Val);
    await handleValidation("option3", result.option3Val);

    if (!submitButton.isReadyToSubmit) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: constant.SMOOTH,
      });
      return false;
    }

    const { vendorDiscount, measurement = {} } = result;
    result.discount = vendorDiscount;

    const { length = {}, width = {}, height = {} } = measurement || {};
    let { value: lengthValue = 0 } = length || {};
    let { value: widthValue = 0 } = width || {};
    let { value: heightValue = 0 } = height || {};

    lengthValue = parseFloat(lengthValue);
    widthValue = parseFloat(widthValue);
    heightValue = parseFloat(heightValue);

    if (lengthValue > MAX_MEASUREMENT || widthValue > MAX_MEASUREMENT || heightValue > MAX_MEASUREMENT) {
      const bannerData = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("section.measurement.length.title"),
      };
      setBanner(bannerData);
      return false;
    }

    // check volumetric weight is greater than 180 or not if yes then through error
    const isLargeVolumetricWeight = baseHelper.isLargeVolumetricWeight(lengthValue, widthValue, heightValue);
    if (isLargeVolumetricWeight) {
      const bannerData = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("section.measurement.weight.title"),
      };
      setBanner(bannerData);
      return false;
    }

    result.measurement = {
      length: {
        value: lengthValue,
        unit: constant.CM,
      },
      width: {
        value: widthValue,
        unit: constant.CM,
      },
      height: {
        value: heightValue,
        unit: constant.CM,
      },
    };

    delete result.length;
    delete result.width;
    delete result.height;

    const notToUpdate = constant.NOT_TO_UPDATE;
    notToUpdate.forEach((element) => {
      delete result[element];
    });

    if (image && image.length && image[0].url) {
      const imageUrl = image[0].url;
      result.image = { url: imageUrl };
    }
    result.id = parseInt(variantId, 10);
    result.productId = productId;

    const isLocalImageUrl = image && image.upload === constant.LOCAL;

    if (!isLocalImageUrl) {
      variantUpdate(result);
    }

    if (isLocalImageUrl) {
      try {
        delete image.upload;
        const res = await uploadImage({ variables: { input: { image: image[0], productId: match.params.productId } } });
        const resData = baseHelper.getResponseData(res.data, gql.UPLOAD_IMAGE);
        if (resData) {
          result.image = { url: resData.imageUrl };
          variantUpdate(result);
        }
      } catch (exception) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: errorHelper.parse(exception),
        });
      }
    }
  };

  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const handleChange = (fieldName, fieldValue) => {
    if (fieldName === SKU || fieldValue > -1 || fieldValue.price > -1) {
      setVariantValue((prevState) => ({
        ...prevState,
        [fieldName]: fieldValue,
      }));
    }
  };

  const handleMeasurementChange = (fieldName, fieldValue) => {
    if (fieldValue > -1) {
      setVariantValue((prevState) => ({
        ...prevState,
        measurement: {
          ...(prevState.measurement || {}),
          [fieldName]: {
            ...((prevState.measurement && prevState.measurement[fieldName]) || {}),
            value: fieldValue,
          },
        },
      }));
    }
  };

  const handleTitleChange = (fieldName, fieldValue) => {
    setVariantValue((prevState) => ({
      ...prevState,
      [fieldName]: fieldValue,
    }));
  };

  return (
    <>
      {banner.isOpen && (
        <>
          <Layout.Section>
            <Banner
              title={banner.title}
              status={banner.status}
              isOpen={banner.isOpen}
              action={banner.action}
              onDismiss={onDismissBanner}
            />
          </Layout.Section>
          <br />
        </>
      )}
      <Layout.AnnotatedSection title={cms("title")}>
        <TitleEdit
          options={options}
          variantData={variantValue}
          handleChange={handleTitleChange}
          handleValidation={handleValidation}
          errorMessage={errorMessage}
          cms={cms}
        />
        <Image productImages={productImages} setImage={setImage} image={image} cms={cms} />
        <Price variantData={variantValue} handleChange={handleChange} cms={cms} />
        <Inventory
          variantData={variantValue}
          handleChange={handleChange}
          handleTitleChange={handleTitleChange}
          cms={cms}
        />
        <Shipping
          variantData={variantValue}
          handleChange={handleChange}
          handleTitleChange={handleTitleChange}
          handleMeasurementChange={handleMeasurementChange}
          cms={cms}
        />

        <PageActions
          primaryAction={{
            content: cms("section.label.button.submit"),
            onAction: () => onSubmit(),
            loading: updateLoading || imageLoading,
          }}
          secondaryActions={{
            content: cms("section.label.button.cancel"),
            onAction: () => history.push(`/products/edit/${productId}`),
            disabled: updateLoading || imageLoading,
          }}
        />
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(ProviderVariantEdit), { feature: constant.ADD_VARIANT });
